import { createSlice, nanoid, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "redux/store";
import { initialState } from "./initial-state";
import { PREFIX_V2, sortByPriorityAndOpenedAt } from "./helper";
import {
	ManagedPopUp,
	OpenPopUpPayload,
	ClosePopUpPayload,
	UpdatePopUpPayload,
	PopUpManagerV2Slice
} from "./types";
import { PopUpVariant } from "interfaces";

const findIndexById = (stack: ManagedPopUp[], id: string) =>
	stack.findIndex((p) => p.id === id);

export const popUpManagerV2Slice = createSlice({
	name: PREFIX_V2,
	initialState,
	reducers: {
		onOpenPopUpV2: (state, action: PayloadAction<OpenPopUpPayload>) => {
			const { variant, id, replaceIfExists, payload, toTop } = action.payload;
			const popupId = id ?? nanoid();
			const existingIndex = id ? findIndexById(state.stack, popupId) : -1;
			if (existingIndex >= 0) {
				if (replaceIfExists) {
					state.stack[existingIndex] = {
						id: popupId,
						variant,
						openedAt: Date.now(),
						payload
					};
				} else if (toTop) {
					const existing = state.stack.splice(existingIndex, 1)[0];
					state.stack.push(existing);
				}

				sortByPriorityAndOpenedAt(state.stack);
				return;
			}
			if (
				state.maxStackSize !== null &&
				state.stack.length >= state.maxStackSize
			) {
				// drop oldest (FIFO) to respect max size
				state.stack.shift();
			}
			state.stack.push({
				id: popupId,
				variant,
				openedAt: Date.now(),
				payload
			});

			sortByPriorityAndOpenedAt(state.stack);
		},
		onClosePopUpV2: (
			state,
			action: PayloadAction<ClosePopUpPayload | undefined>
		) => {
			const payload = action.payload;
			if (!payload) {
				state.stack.pop();
				return;
			}
			const { id, variant, allOfVariant } = payload;
			if (allOfVariant && variant) {
				state.stack = state.stack.filter((p) => p.variant !== variant);
				return;
			}
			if (id) {
				state.stack = state.stack.filter((p) => p.id !== id);
				return;
			}
			if (variant) {
				// close first from top that matches variant
				for (let i = state.stack.length - 1; i >= 0; i--) {
					if (state.stack[i].variant === variant) {
						state.stack.splice(i, 1);
						break;
					}
				}
			}
		},
		onCloseAllPopUpsV2: (state) => {
			state.stack = [];
		},
		onUpdatePopUpV2: (state, action: PayloadAction<UpdatePopUpPayload>) => {
			const { id, patch } = action.payload;
			const index = findIndexById(state.stack, id);
			if (index >= 0) {
				state.stack[index] = {
					...state.stack[index],
					...patch
				} as ManagedPopUp;
			}
		},
		onReplaceAllPopUpsV2: (state, action: PayloadAction<PopUpVariant[]>) => {
			state.stack = action.payload.map((v) => ({
				id: nanoid(),
				variant: v,
				openedAt: Date.now()
			}));
			sortByPriorityAndOpenedAt(state.stack);
		}
	}
});

export const {
	onOpenPopUpV2,
	onClosePopUpV2,
	onCloseAllPopUpsV2,
	onUpdatePopUpV2,
	onReplaceAllPopUpsV2
} = popUpManagerV2Slice.actions;

// Selector: note store must register popUpManagerV2 reducer key (not done yet here)
export const selectPopUpManagerV2State = (
	state: RootState
): PopUpManagerV2Slice =>
	(state as unknown as { popUpManagerV2?: PopUpManagerV2Slice })
		.popUpManagerV2 ?? initialState;
export default popUpManagerV2Slice.reducer;
export * from "./types";
